import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Quote,
  Link as LinkIcon, Heading1, Heading2, Heading3,
  AlignLeft, AlignCenter, AlignRight, Minus, Undo2, Redo2, Type,
} from 'lucide-react';

/**
 * Reusable WYSIWYG rich-text editor based on TipTap.
 *
 * Props:
 *   - value     : initial HTML string
 *   - onChange  : (html) => void  — called on every change
 *   - dir       : 'ltr' | 'rtl' | 'auto' (default 'auto')
 *   - minHeight : tailwind class for editor height (default 'min-h-[200px]')
 *   - placeholder: shown via CSS when empty (optional)
 *
 * Output is HTML (sanitized server-side via bleach + frontend via DOMPurify
 * at render time). Emits empty string when the editor is empty (instead of
 * the TipTap default "<p></p>") so backend can store NULL.
 */
const ToolbarButton = ({ active, onClick, title, disabled, children, testid }) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    className={[
      'p-1.5 rounded transition-colors text-text-secondary hover:text-text-primary',
      'hover:bg-surface-elevated disabled:opacity-40 disabled:cursor-not-allowed',
      active ? 'bg-primary/15 text-primary' : '',
    ].join(' ')}
  >
    {children}
  </button>
);

const Sep = () => <div className="w-px h-5 bg-border mx-1" />;

const RichTextEditor = ({
  value = '',
  onChange,
  dir = 'auto',
  minHeight = 'min-h-[220px]',
  placeholder,
  testid = 'rich-text-editor',
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      TextStyle,
      Color,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: [
          'prose prose-invert max-w-none px-3 py-2 focus:outline-none',
          minHeight,
          'prose-headings:font-serif prose-headings:text-text-primary',
          'prose-p:text-text-primary prose-strong:text-text-primary',
          'prose-blockquote:text-text-secondary prose-blockquote:border-primary/40',
          'prose-a:text-primary',
        ].join(' '),
        ...(dir !== 'auto' ? { dir } : {}),
        'data-testid': `${testid}-content`,
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      // TipTap returns "<p></p>" for empty editor → emit empty string instead
      const out = ed.isEmpty ? '' : html;
      onChange?.(out);
    },
  });

  // Sync external value changes (e.g. when editing different prophets)
  useEffect(() => {
    if (!editor) return;
    if ((value || '') !== editor.getHTML()) {
      editor.commands.setContent(value || '', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) {
    return <div className="border border-border rounded bg-surface px-3 py-2 text-text-muted">Chargement…</div>;
  }

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href || '';
    // eslint-disable-next-line no-alert
    const url = window.prompt('URL du lien (laisser vide pour retirer)', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    let safe = url;
    if (!/^(https?:|mailto:|tel:)/i.test(safe)) safe = 'https://' + safe;
    editor.chain().focus().extendMarkRange('link').setLink({ href: safe }).run();
  };

  return (
    <div
      className="border border-border rounded bg-surface focus-within:border-primary/50 transition-colors"
      data-testid={testid}
    >
      <div className="flex flex-wrap items-center gap-0.5 p-1.5 border-b border-border bg-surface-elevated/30">
        <ToolbarButton testid="rt-h1" title="Titre 1" active={editor.isActive('heading', { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
          <Heading1 className="w-4 h-4" />
        </ToolbarButton>        <ToolbarButton testid="rt-h2" title="Titre 2" active={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <Heading2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton testid="rt-h3" title="Titre 3" active={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          <Heading3 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton testid="rt-paragraph" title="Paragraphe" active={editor.isActive('paragraph')}
          onClick={() => editor.chain().focus().setParagraph().run()}>
          <Type className="w-4 h-4" />
        </ToolbarButton>
        <Sep />
        <ToolbarButton testid="rt-bold" title="Gras (Ctrl+B)" active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton testid="rt-italic" title="Italique (Ctrl+I)" active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton testid="rt-underline" title="Souligné (Ctrl+U)" active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}>
          <UnderlineIcon className="w-4 h-4" />
        </ToolbarButton>
        <Sep />
        <ToolbarButton testid="rt-bullet" title="Liste à puces" active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton testid="rt-ordered" title="Liste numérotée" active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton testid="rt-quote" title="Citation" active={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          <Quote className="w-4 h-4" />
        </ToolbarButton>
        <Sep />
        <ToolbarButton testid="rt-align-left" title="Aligner à gauche" active={editor.isActive({ textAlign: 'left' })}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}>
          <AlignLeft className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton testid="rt-align-center" title="Centrer" active={editor.isActive({ textAlign: 'center' })}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}>
          <AlignCenter className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton testid="rt-align-right" title="Aligner à droite" active={editor.isActive({ textAlign: 'right' })}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}>
          <AlignRight className="w-4 h-4" />
        </ToolbarButton>
        <Sep />
        <ToolbarButton testid="rt-link" title="Lien" active={editor.isActive('link')} onClick={setLink}>
          <LinkIcon className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton testid="rt-hr" title="Séparateur"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}>
          <Minus className="w-4 h-4" />
        </ToolbarButton>
        <Sep />
        <label className="flex items-center" title="Couleur du texte">
          <input
            type="color"
            onInput={(e) => editor.chain().focus().setColor(e.target.value).run()}
            value={editor.getAttributes('textStyle').color || '#e5e5e5'}
            className="w-6 h-6 p-0 border-0 rounded cursor-pointer bg-transparent"
            data-testid="rt-color"
          />
        </label>
        <ToolbarButton testid="rt-color-clear" title="Couleur par défaut"
          onClick={() => editor.chain().focus().unsetColor().run()}>
          <span className="text-[10px] font-bold">A↺</span>
        </ToolbarButton>
        <Sep />
        <ToolbarButton testid="rt-undo" title="Annuler" disabled={!editor.can().undo()}
          onClick={() => editor.chain().focus().undo().run()}>
          <Undo2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton testid="rt-redo" title="Rétablir" disabled={!editor.can().redo()}
          onClick={() => editor.chain().focus().redo().run()}>
          <Redo2 className="w-4 h-4" />
        </ToolbarButton>
      </div>
      <div className="relative">
        <EditorContent editor={editor} />
        {placeholder && editor.isEmpty && (
          <div className="absolute inset-x-0 top-0 px-3 py-2 pointer-events-none text-text-muted italic text-sm">
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
};

export default RichTextEditor;
